/**
 * Resolving a feature across a multi-seat selection.
 *
 * Two levels are in play, and they live in different places:
 *   - the product-access level value, on the cost centre at productAccess.features
 *   - per-seat overrides, from ADMIN_GET_FEATURES_FOR_PRODUCT_ACCESS
 *
 * The product-access level is a master gate: a seat may narrow access but never widen it.
 * A seat that says enabled while the licence says disabled is still off.
 */

export interface SeatFeatureRow {
  product_feature_id: string;
  user_id: string;
  feature_name: string;
  value_type: string;
  value: string;
  enabled: boolean;
}

export interface LicenseFeature {
  productFeatureId?: string;
  name?: string;
  valueType?: string;
  value?: unknown;
  enabled?: boolean;
}

export type SelectionState = 'on' | 'off';

/**
 * The product-access level entry for a feature, or null when the licence does not have it.
 * `features` is the map from productAccess.features, keyed by ProductAccessFeature id.
 */
export function findLicenseFeature(
  features: Record<string, LicenseFeature> | undefined | null,
  featureName: string,
): LicenseFeature | null {
  if (!features) {
    return null;
  }
  return Object.values(features).find((feature) => feature.name === featureName) || null;
}

/** True when the licence grants the feature at all. Everything below is gated on this. */
export function isLicenseEnabled(
  features: Record<string, LicenseFeature> | undefined | null,
  featureName: string,
): boolean {
  return !!findLicenseFeature(features, featureName)?.enabled;
}

/**
 * Effective state for one seat: its override if it has one, otherwise the licence value.
 * Returns false whenever the licence is off, regardless of the seat's own row.
 */
export function seatEffectiveState(
  licenseEnabled: boolean,
  seatRows: SeatFeatureRow[],
  userId: string,
  featureName: string,
): boolean {
  if (!licenseEnabled) {
    return false;
  }
  const override = seatRows.find(
    (row) => row.user_id === userId && row.feature_name === featureName,
  );
  return override ? override.enabled : true;
}

/**
 * Collapses a multi-seat selection into one control state.
 *
 * 'on' only when every selected seat resolves on. A selection whose seats disagree reads as
 * 'off', so a single click turns the whole selection on.
 */
export function selectionState(
  licenseEnabled: boolean,
  seatRows: SeatFeatureRow[],
  userIds: string[],
  featureName: string,
): SelectionState {
  if (!userIds.length) {
    return 'off';
  }
  return userIds.every((userId) =>
    seatEffectiveState(licenseEnabled, seatRows, userId, featureName),
  )
    ? 'on'
    : 'off';
}

export interface SelectionSummary {
  total: number;
  enabled: number;
  disabled: number;
  /** The selected seats do not agree, so the control cannot represent them all. */
  mixed: boolean;
}

/**
 * Counts how the selected seats currently resolve, for explaining a disagreement the toggle
 * itself flattens: it reads off whenever the seats differ, which understates a selection that
 * is mostly enabled.
 */
export function selectionSummary(
  licenseEnabled: boolean,
  seatRows: SeatFeatureRow[],
  userIds: string[],
  featureName: string,
): SelectionSummary {
  const enabled = userIds.filter((userId) =>
    seatEffectiveState(licenseEnabled, seatRows, userId, featureName),
  ).length;
  const total = userIds.length;

  return {
    total,
    enabled,
    disabled: total - enabled,
    mixed: enabled > 0 && enabled < total,
  };
}

/** Toggling applies one explicit value to every selected seat. */
export function nextStateOnToggle(current: SelectionState): boolean {
  return current !== 'on';
}
